/* eslint-disable eqeqeq */
import React, { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { observer } from 'mobx-react-lite';
import { Field, Form } from 'mobx-react-form';
import { isEqual, multiply, sum, toNumber } from 'lodash';

import {
  RentRollTableColumn,
  ResidentialRentRollUnit,
  UnitLayouts,
  OutdoorSpaces,
  RentTypes,
  ResidentialLeaseStatusTypes,
} from 'types/underwriting/income-approach';
import { formatFloat } from 'components/underwriting/utils/formatters';
import { convertUndefinedToNull } from 'components/underwriting/utils/helpers';
import { renderSingleSelectCell } from 'components/underwriting/utils/cell-renders';
import { divide } from 'shared/utils/numberOperations';
import { formatCurrencyFloat } from 'shared/utils/numberFormatters';
import { Table } from 'components/shared/Table';
import { DISABLE_TABLE_NUMBER_INPUT_CONTROLS_SX } from '../../constants';

const getRentTotals = (units: ResidentialRentRollUnit[] = []) => {
  const rentTotalMonthly = units.reduce((total: number, unit: ResidentialRentRollUnit) => {
    return sum([total, unit.rent || 0]);
  }, 0);

  const rentTotalAnnual = multiply(rentTotalMonthly, 12);
  const projectedRentTotalMonthly = units.reduce((total: number, unit: ResidentialRentRollUnit) => {
    return sum([total, unit.projectedRent || 0]);
  }, 0);

  const projectedRentTotalAnnual = multiply(projectedRentTotalMonthly, 12);

  return { rentTotalMonthly, rentTotalAnnual, projectedRentTotalMonthly, projectedRentTotalAnnual };
};

interface RentRollTableProps {
  columns: RentRollTableColumn[];
  isProjectedRentRoll?: boolean;
  form: Form;
}

const RentRollTable = observer(({ columns, form, isProjectedRentRoll = false }: RentRollTableProps) => {
  const rentRollField: Field = form.$('incomeApproach.residential.rentRoll');
  const rentRoll: ResidentialRentRollUnit[] = rentRollField.value;

  const handleProcessRowUpdate = (newRow: any, oldRow: any) => {
    if (newRow.isTotalRow || isEqual(newRow, oldRow)) {
      return oldRow;
    }

    const currentRentRoll = rentRollField.value;
    const targetIndex = currentRentRoll.findIndex((unit: ResidentialRentRollUnit) => {
      return unit.id == oldRow.id;
    });
    if (targetIndex > -1) {
      /*
       * This is a workaround to ensure mobx-react-form updates properly numbers
       * If `undefined` is passed to `number` field - update will be ignored, causing mismatch in form and table data.
       */
      const rent = newRow.rent || 0;
      const projectedRent = isProjectedRentRoll ? newRow.projectedRent : rent;
      const sanitizedRow = convertUndefinedToNull({
        ...newRow,
        rent,
        projectedRent,
      });
      currentRentRoll[targetIndex] = sanitizedRow;
      rentRollField.set(currentRentRoll);

      return sanitizedRow;
    } else {
      return oldRow;
    }
  };

  const gridColumns: GridColDef[] = useMemo(() => {
    return columns.map((column) => {
      const columnName = column.data || '';

      let width = column.data === 'index' ? 20 : 120;
      let align: 'left' | 'right' | 'center' = 'left';
      let editable = !isProjectedRentRoll;
      let type: 'string' | 'number' | 'singleSelect' = 'string';
      let valueOptions: string[] = [];
      let valueFormatter: ((value: any) => string) | undefined;
      let renderCell: ((params: any) => React.ReactNode) | undefined;

      switch (columnName) {
        case 'index':
          editable = false;
          align = 'right';
          renderCell = (params: any) => {
            return params.row.index != null ? params.row.index + 1 : undefined;
          };
          break;
        case 'bedrooms':
        case 'bathrooms':
          type = 'number';
          align = 'right';
          width = 90;
          break;
        case 'squareFootage':
          type = 'number';
          align = 'right';
          width = 100;
          valueFormatter = (value: any) => {
            if (value == null || value === '') return '';
            return formatFloat(value);
          };
          break;
        case 'rent':
          type = 'number';
          align = 'right';
          valueFormatter = (value: any) => {
            if (value == null || value === '') return '';
            return formatCurrencyFloat(value);
          };
          break;
        case 'projectedRent':
          type = 'number';
          align = 'right';
          editable = true;
          valueFormatter = (value: any) => {
            if (value == null || value === '') return '';
            return formatCurrencyFloat(value);
          };
          break;
        case 'unitLayout':
          type = 'singleSelect';
          valueOptions = Object.values(UnitLayouts);
          renderCell = renderSingleSelectCell;
          break;
        case 'outdoor':
          type = 'singleSelect';
          valueOptions = Object.values(OutdoorSpaces);
          renderCell = renderSingleSelectCell;
          break;
        case 'rentType':
          width = isProjectedRentRoll ? 130 : 150;
          type = 'singleSelect';
          valueOptions = Object.values(RentTypes);
          renderCell = renderSingleSelectCell;
          break;
        case 'leaseStatus':
          width = isProjectedRentRoll ? 120 : 150;
          type = 'singleSelect';
          valueOptions = Object.values(ResidentialLeaseStatusTypes);
          renderCell = renderSingleSelectCell;
          break;
        case 'rentPerSF':
          type = 'number';
          align = 'right';
          width = isProjectedRentRoll ? 150 : 100;
          editable = false;
          renderCell = (params: any) => {
            if (params.row.isTotalRow) {
              return '';
            }

            const rent = toNumber(isProjectedRentRoll ? params.row.projectedRent : params.row.rent) || 0;
            const squareFootage = toNumber(params.row.squareFootage || 0);
            const rentPerSF = divide(rent, squareFootage);

            return formatCurrencyFloat(rentPerSF);
          };
          break;
        default:
          break;
      }

      return {
        field: columnName,
        headerName: column.value,
        width,
        align,
        editable,
        type,
        valueOptions: valueOptions.length > 0 ? valueOptions : undefined,
        valueFormatter,
        renderCell,
      };
    });
  }, [columns, isProjectedRentRoll]);

  const rowsWithIds = useMemo(() => {
    const { rentTotalMonthly, rentTotalAnnual, projectedRentTotalMonthly, projectedRentTotalAnnual } =
      getRentTotals(rentRoll);
    const dataRows = (rentRoll || []).map((unit, index) => ({ ...unit, index }));

    const totalRows = [
      {
        id: 'monthly-total',
        number: 'Monthly Total',
        rent: rentTotalMonthly,
        projectedRent: projectedRentTotalMonthly,
        isTotalRow: true,
      },
      {
        id: 'annual-total',
        number: 'Annual Total',
        rent: rentTotalAnnual,
        projectedRent: projectedRentTotalAnnual,
        isTotalRow: true,
      },
    ];

    return [...dataRows, ...totalRows];
  }, [rentRoll]);

  return (
    <div>
      <Table
        rows={rowsWithIds}
        columns={gridColumns}
        processRowUpdate={handleProcessRowUpdate}
        onProcessRowUpdateError={(error) => {
          console.log({ error });
        }}
        getCellClassName={(params) => {
          if (params.field === 'rentPerSF' || (isProjectedRentRoll && params.field !== 'projectedRent')) {
            return 'disabled-cell';
          }
          return '';
        }}
        sx={{
          width: 'fit-content',
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #e0e0e0',
            borderRight: '1px solid #f0f0f0',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f5f5f5',
            borderBottom: '2px solid #e0e0e0',
          },

          '& .MuiDataGrid-row[data-id="monthly-total"], & .MuiDataGrid-row[data-id="annual-total"]': {
            backgroundColor: 'rgb(245, 245, 245)',
            '& .MuiDataGrid-cell': {
              backgroundColor: 'rgb(245, 245, 245)',
              color: '#666',
              fontWeight: 'bold',
            },
          },
          '& .disabled-cell': {
            backgroundColor: 'rgb(245, 245, 245)',
            color: '#666',
            fontWeight: 'normal',
          },
          ...DISABLE_TABLE_NUMBER_INPUT_CONTROLS_SX,
        }}
        isCellEditable={(params) => {
          if (params.row.isTotalRow) {
            return false;
          }

          if (params.field === 'rentPerSF') {
            return false;
          }

          return true;
        }}
      />
    </div>
  );
});

export default RentRollTable;
